import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Box,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    Button,
    Paper
} from '@mui/material';
import { PartnershipScenario, Industry, RiskAssessment } from '../types/types';
import { usePartnership } from '../contexts/PartnershipContext';
import IndustrySelector, { IndustryTemplateValues } from './IndustrySelector';
import RiskAssessmentSlider from './RiskAssessmentSlider';

interface PartnershipFormProps {
    onSubmit?: (data: Omit<PartnershipScenario, 'id'>) => void;
    initialData?: PartnershipScenario;
}

const PartnershipForm: React.FC<PartnershipFormProps> = ({ onSubmit, initialData }) => {
    const { addScenario } = usePartnership();
    const { control, handleSubmit, setValue, formState: { errors } } = useForm<Omit<PartnershipScenario, 'id'>>({
        defaultValues: initialData || {
            name: '',
            partnerType: 'Integration',
            timeframe: 1,
            industry: 'Custom',
            riskAssessment: 'Realistic',
            investmentMetrics: {
                partnerAcquisitionCosts: 0,
                partnerManagementCosts: 0,
                partnerMarketingCosts: 0,
                revenueSharePercentage: 0,
                technologySetupCosts: 0,
                trainingCosts: 0
            },
            returnMetrics: {
                projectedRevenue: 0,
                leadGenerationVolume: 0,
                leadConversionRate: 0,
                customerRetentionImprovement: 0,
                averageDealSize: 0,
                salesCycleReduction: 0,
                salesCycleLengthMonths: 3,
                marketExpansionPotential: 'medium'
            },
            riskMetrics: {
                partnerChurnProbability: 0,
                marketVolatilityImpact: 'medium',
                resourceAllocationRisk: 0,
                dependencyRisk: 0
            }
        }
    });

    const onFormSubmit = (data: Omit<PartnershipScenario, 'id'>) => {
        addScenario(data);
        if (onSubmit) {
            onSubmit(data);
        }
    };

    const handleApplyIndustryTemplate = (tpl: IndustryTemplateValues) => {
        setValue('returnMetrics.leadConversionRate', tpl.leadConversionRate, { shouldValidate: true });
        setValue('returnMetrics.averageDealSize', tpl.averageDealSize, { shouldValidate: true });
        setValue('returnMetrics.salesCycleLengthMonths', tpl.salesCycleLengthMonths, { shouldValidate: true });
    };

    // Watching fields not needed here; values consumed within controlled components

    return (
        <Paper elevation={3} sx={{ p: 3 }}>
            <form onSubmit={handleSubmit(onFormSubmit)} aria-label="Partnership ROI form">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom sx={{ 
                            fontSize: '2.5rem',
                            fontWeight: 800,
                            color: '#33bbdf',
                            mb: 4,
                            textAlign: 'center',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            Partnership ROI Calculator
                        </Typography>
                    </Grid>

                    {/* Basic Information */}
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: 'Scenario name is required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Partnership Name"
                                    fullWidth
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="industry"
                            control={control}
                            render={({ field }) => (
                                <IndustrySelector
                                    value={(field.value as Industry) || 'Custom'}
                                    onChange={(val) => field.onChange(val)}
                                    onApplyTemplate={handleApplyIndustryTemplate}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ 
                                fontSize: '1.3rem',
                                fontWeight: 600,
                                color: '#33bbdf',
                                '&.Mui-focused': {
                                    color: '#33bbdf'
                                }
                            }} id="partner-type-label">Partner Type</InputLabel>
                            <Controller
                                name="partnerType"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        aria-labelledby="partner-type-label"
                                        label="Partner Type"
                                        sx={{
                                            '& .MuiSelect-select': {
                                                fontSize: '1.1rem'
                                            }
                                        }}
                                    >
                                        <MenuItem value="Integration" sx={{ fontSize: '1.1rem' }}>Integration</MenuItem>
                                        <MenuItem value="Reseller" sx={{ fontSize: '1.1rem' }}>Reseller</MenuItem>
                                        <MenuItem value="Referral" sx={{ fontSize: '1.1rem' }}>Referral</MenuItem>
                                        <MenuItem value="Strategic" sx={{ fontSize: '1.1rem' }}>Strategic</MenuItem>
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="timeframe"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Timeframe (years)"
                                    type="number"
                                    fullWidth
                                    InputProps={{ inputProps: { min: 1, max: 10 } }}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Investment Metrics */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ 
                            fontSize: '1.8rem',
                            fontWeight: 700,
                            color: '#33bbdf',
                            mt: 4,
                            mb: 3,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            INVESTMENT METRICS - One-time costs: Year 1
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="investmentMetrics.partnerAcquisitionCosts"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Partner Acquisition Costs (one-time)"
                                    type="number"
                                    fullWidth
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="investmentMetrics.partnerManagementCosts"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Partner Management Personnel (annual)"
                                    type="number"
                                    fullWidth
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="investmentMetrics.partnerMarketingCosts"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Partner Marketing Budget (annual)"
                                    type="number"
                                    fullWidth
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="investmentMetrics.revenueSharePercentage"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Revenue Share Percentage"
                                    type="number"
                                    fullWidth
                                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>



                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="investmentMetrics.trainingCosts"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Partner Training & Onboarding (one-time)"
                                    type="number"
                                    fullWidth
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="investmentMetrics.technologySetupCosts"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Technology Setup & Integration (one-time)"
                                    type="number"
                                    fullWidth
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Return Metrics */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ 
                            fontSize: '1.8rem',
                            fontWeight: 700,
                            color: '#33bbdf',
                            mt: 4,
                            mb: 3,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Return Metrics
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="returnMetrics.projectedRevenue"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Projected Revenue"
                                    type="number"
                                    fullWidth
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="returnMetrics.leadGenerationVolume"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Lead Generation Volume"
                                    type="number"
                                    fullWidth
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="returnMetrics.leadConversionRate"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Lead Conversion Rate (%)"
                                    type="number"
                                    fullWidth
                                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="returnMetrics.customerRetentionImprovement"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Customer Retention Improvement (%)"
                                    type="number"
                                    fullWidth
                                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="returnMetrics.averageDealSize"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Average Deal Size"
                                    type="number"
                                    fullWidth
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="returnMetrics.salesCycleLengthMonths"
                            control={control}
                            rules={{ min: { value: 1, message: 'Minimum 1 month' } }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Sales Cycle Length (months)"
                                    type="number"
                                    fullWidth
                                    error={!!errors.returnMetrics?.salesCycleLengthMonths}
                                    helperText={errors.returnMetrics?.salesCycleLengthMonths?.message}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="returnMetrics.salesCycleReduction"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Sales Cycle Reduction (%)"
                                    type="number"
                                    fullWidth
                                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ 
                                fontSize: '1.3rem',
                                fontWeight: 600,
                                color: '#33bbdf',
                                '&.Mui-focused': {
                                    color: '#33bbdf'
                                }
                            }}>Market Expansion Potential</InputLabel>
                            <Controller
                                name="returnMetrics.marketExpansionPotential"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        label="Market Expansion Potential"
                                        sx={{
                                            '& .MuiSelect-select': {
                                                fontSize: '1.1rem'
                                            }
                                        }}
                                    >
                                        <MenuItem value="low" sx={{ fontSize: '1.1rem' }}>Low</MenuItem>
                                        <MenuItem value="medium" sx={{ fontSize: '1.1rem' }}>Medium</MenuItem>
                                        <MenuItem value="high" sx={{ fontSize: '1.1rem' }}>High</MenuItem>
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>

                    {/* Risk Metrics */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ 
                            fontSize: '1.8rem',
                            fontWeight: 700,
                            color: '#33bbdf',
                            mt: 4,
                            mb: 3,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Risk Metrics
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="riskMetrics.partnerChurnProbability"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Partner Churn Probability (%)"
                                    type="number"
                                    fullWidth
                                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ 
                                fontSize: '1.3rem',
                                fontWeight: 600,
                                color: '#33bbdf',
                                '&.Mui-focused': {
                                    color: '#33bbdf'
                                }
                            }}>Market Volatility Impact</InputLabel>
                            <Controller
                                name="riskMetrics.marketVolatilityImpact"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        label="Market Volatility Impact"
                                        sx={{
                                            '& .MuiSelect-select': {
                                                fontSize: '1.1rem'
                                            }
                                        }}
                                    >
                                        <MenuItem value="low" sx={{ fontSize: '1.1rem' }}>Low</MenuItem>
                                        <MenuItem value="medium" sx={{ fontSize: '1.1rem' }}>Medium</MenuItem>
                                        <MenuItem value="high" sx={{ fontSize: '1.1rem' }}>High</MenuItem>
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="riskMetrics.resourceAllocationRisk"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Resource Allocation Risk (%)"
                                    type="number"
                                    fullWidth
                                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="riskMetrics.dependencyRisk"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Dependency Risk (%)"
                                    type="number"
                                    fullWidth
                                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '1.3rem',
                                            fontWeight: 600,
                                            color: '#33bbdf',
                                            '&.Mui-focused': {
                                                color: '#33bbdf'
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.2rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Risk Assessment Slider */}
                    <Grid item xs={12}>
                        <Controller
                            name="riskAssessment"
                            control={control}
                            render={({ field }) => (
                                <RiskAssessmentSlider value={field.value as RiskAssessment} onChange={field.onChange} />
                            )}
                        />
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                sx={{
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    py: 2,
                                    px: 4,
                                    backgroundColor: '#33bbdf',
                                    color: 'white',
                                    borderRadius: '12px',
                                    boxShadow: '0 8px 25px rgba(51, 187, 223, 0.3)',
                                    transition: 'all 0.3s ease',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    '&:hover': {
                                        backgroundColor: '#2a9bb8',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 35px rgba(51, 187, 223, 0.4)'
                                    }
                                }}
                            >
                                Calculate ROI
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Paper>
    );
};

export default PartnershipForm; 